import ReactMarkdown from "react-markdown";

import { MARKDOWN_REMARK_PLUGINS } from "@/lib/markdown";

export function SilkDivider({ label = "THỈNH GIÁO TIẾP" }: { label?: string }) {
  return (
    <div className="my-8 flex items-center gap-3" aria-hidden="true">
      <div className="h-px flex-1 bg-gold-soft/60" />
      <span className="font-display text-[9px] tracking-[0.3em] text-primary/70">{label}</span>
      <div className="h-px flex-1 bg-gold-soft/60" />
    </div>
  );
}

export function SilkReadingMarkdown({
  body,
  density = "default",
  tableLayout = "scroll",
}: {
  body: string;
  density?: "default" | "compact";
  tableLayout?: "scroll" | "palm-comparison";
}) {
  const compact = density === "compact";
  const comparison = tableLayout === "palm-comparison";
  // Một số bài luận cũ lưu newline dưới dạng chuỗi "\\n". Chuẩn hoá trước khi
  // đưa vào parser để heading, list và các đoạn văn trở lại đúng cấu trúc Markdown.
  const markdown = body.replace(/\\r\\n/g, "\n").replace(/\\n/g, "\n");

  return (
    <div
      className={`palm-reading-markdown prose prose-sm max-w-none font-sans font-normal text-foreground/88 dark:prose-invert dark:text-[#EAE0D5] ${compact ? "text-[14px] leading-[1.7] md:text-[15px]" : "text-[15px] leading-[1.8] md:text-base"}`}
    >
      <ReactMarkdown
        remarkPlugins={MARKDOWN_REMARK_PLUGINS}
        components={{
          p: ({ children }) => (
            <p className={`${compact ? "mb-4 leading-[1.7]" : "mb-6 leading-[1.8]"} last:mb-0`}>
              {children}
            </p>
          ),
          strong: ({ children }) => (
            <strong className="font-medium text-primary">{children}</strong>
          ),
          em: ({ children }) => (
            <em className="font-serif-soft italic text-foreground/80">{children}</em>
          ),
          ul: ({ children }) => (
            <ul
              className={`${compact ? "my-5 gap-3" : "my-8 gap-4"} flex list-none flex-col !ml-0 !pl-0`}
            >
              {children}
            </ul>
          ),
          li: ({ children }) => (
            <li className="flex items-start gap-4 !my-0 !pl-0 !ml-0 leading-[1.7]">
              <span className="mt-[4px] select-none text-[13px] text-primary/80">✦</span>
              <div className="min-w-0 flex-1">{children}</div>
            </li>
          ),
          ol: ({ children }) => (
            <ol
              className={`${compact ? "my-5 gap-3" : "my-8 gap-4"} flex list-decimal flex-col pl-6 marker:text-primary`}
            >
              {children}
            </ol>
          ),
          h2: ({ children }) => (
            <>
              <SilkDivider label="♦ ♦ ♦" />
              <h2
                className={`${compact ? "mb-4 mt-6" : "mb-6 mt-8"} font-display text-base font-medium leading-snug tracking-[0.08em] text-primary md:text-lg`}
              >
                {children}
              </h2>
            </>
          ),
          h3: ({ children }) => (
            <>
              <SilkDivider label="♦ ♦ ♦" />
              <h3
                className={`${compact ? "mb-3 mt-5" : "mb-5 mt-8"} font-display text-sm font-medium leading-snug tracking-[0.08em] text-primary md:text-base`}
              >
                {children}
              </h3>
            </>
          ),
          blockquote: ({ children }) => (
            <blockquote
              className={`${compact ? "my-4" : "my-6"} border-l-2 border-primary/50 pl-4 font-serif-soft italic leading-7 text-foreground/75`}
            >
              {children}
            </blockquote>
          ),
          hr: () => <hr className="my-6 border-gold-soft/40" />,
          code: ({ children }) => (
            <code className="rounded bg-primary/8 px-1 py-0.5 text-[0.9em] text-primary">
              {children}
            </code>
          ),
          // Bảng so sánh (vd "Đối chiếu hai tay" của bói chỉ tay) có 3 cột chữ
          // Việt — trên màn 375px là chật, nên cuộn ngang TRONG khung bảng thay vì
          // để cả trang trượt ngang.
          table: ({ children }) => (
            <div
              className={`${compact ? "my-5" : "my-8"} ${comparison ? "palm-comparison-wrap overflow-hidden" : "overflow-x-auto"} rounded-sm border border-gold-soft/40`}
            >
              <table
                className={`w-full border-collapse text-[13px] md:text-sm ${comparison ? "palm-comparison-table min-w-0" : "min-w-[440px]"}`}
              >
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-gold-soft/10 border-b border-gold-soft/50">{children}</thead>
          ),
          tr: ({ children }) => (
            <tr className="border-b border-gold-soft/20 last:border-0">{children}</tr>
          ),
          th: ({ children }) => (
            <th className="px-3 py-2.5 text-left font-display text-[11px] font-medium uppercase tracking-[0.1em] text-primary">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-3 py-2.5 align-top leading-[1.65]">{children}</td>
          ),
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
